import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Switch,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { usePWAStatus } from '../../hooks/usePWAStatus';
import {
    isPushNotificationSupported,
    isNotificationPermissionGranted,
    requestNotificationPermission,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    sendSubscriptionToServer,
    removeSubscriptionFromServer,
    getCurrentPushSubscription,
    testPushNotification,
    isPWAWithPushEnabled
} from '../../utils/pushNotifications';

/**
 * Push Notification Setup Component
 * Handles push notification permissions and subscription management
 */
const PushNotificationSetup = ({ open, onClose }) => {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [hasSubscription, setHasSubscription] = useState(false);
    
    const { isStandalone, isAddToHomeScreenSupported } = usePWAStatus();

    useEffect(() => {
        checkPushStatus();
    }, [open]);

    const checkPushStatus = async () => {
        try {
            const subscription = await getCurrentPushSubscription();
            const hasPermission = isNotificationPermissionGranted();
            setHasSubscription(subscription !== null);
            setPushEnabled(hasPermission && subscription !== null);
        } catch (error) {
            console.error('Error checking push status:', error);
        }
    };

    const handleTogglePush = async () => {
        if (pushEnabled) {
            await disablePushNotifications();
        } else {
            await enablePushNotifications();
        }
    };

    const enablePushNotifications = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Step 1: Request permission
            const permission = await requestNotificationPermission();
            
            if (permission !== 'granted') {
                throw new Error('Notification permission denied');
            }

            // Step 2: Subscribe to push notifications
            const subscription = await subscribeToPushNotifications();

            // Step 3: Send subscription to server
            await sendSubscriptionToServer(subscription);

            setPushEnabled(true);
            setHasSubscription(true);
            setSuccess('Push notifications enabled successfully!');
        } catch (error) {
            console.error('Error enabling push notifications:', error);
            setError(error.message || 'Failed to enable push notifications');
        } finally {
            setLoading(false);
        }
    };

    const disablePushNotifications = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const currentSubscription = await getCurrentPushSubscription();
            if (currentSubscription) {
                // Step 1: Remove current device subscription from server
                await removeSubscriptionFromServer(currentSubscription);
            }

            // Step 2: Unsubscribe current device from browser push
            await unsubscribeFromPushNotifications();

            setPushEnabled(false);
            setHasSubscription(false);
            setSuccess('Push notifications disabled successfully!');
        } catch (error) {
            console.error('Error disabling push notifications:', error);
            setError(error.message || 'Failed to disable push notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleTestNotification = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await testPushNotification();
            setSuccess('Test notification sent! Check your device.');
        } catch (error) {
            console.error('Error sending test notification:', error);
            setError(error.message || 'Failed to send test notification');
        } finally {
            setLoading(false);
        }
    };

    const isSupported = isPushNotificationSupported();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <NotificationsIcon />
                    Enable Push Notifications
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {!isSupported && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Push notifications are not supported in this browser.
                    </Alert>
                )}

                {!isStandalone && isAddToHomeScreenSupported && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIphoneIcon />
                            <Typography variant="body2">
                                For the best notification experience, add this app to your home screen first.
                            </Typography>
                        </Box>
                    </Alert>
                )}


                <Typography variant="body1" sx={{ mb: 2 }}>
                    Enable push notifications to receive important alerts even when the app is closed.
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={pushEnabled}
                                onChange={handleTogglePush}
                                disabled={!isSupported || loading}
                            />
                        }
                        label={pushEnabled ? "Push notifications enabled" : "Push notifications disabled"}
                    />
                </Box>

                {pushEnabled && hasSubscription && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleTestNotification}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : <NotificationsIcon />}
                        >
                            Test Notification
                        </Button>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PushNotificationSetup;
